import { useState } from 'react';
import useAuthenticatedApi from '../../Authentication/components/hooks/useAuthenticatedApi';

export interface GlobalSettings {
    id: number;
    system_gallery_base_url: string;
    system_gallery_alpha_url: string;
    updated_at: string;
}

export interface UpdateGlobalSettingsRequest {
    systemGalleryBaseUrl: string;
    systemGalleryAlphaUrl: string;
}

export default function useGlobalSettings() {
    const authenticatedApi = useAuthenticatedApi();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const getGlobalSettings = async (): Promise<GlobalSettings | null> => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await authenticatedApi.get('/global-settings');
            return response.data.data;
        } catch (err: any) {
            console.error('Error fetching global settings:', err);
            setError(
                err.response?.data?.message ||
                    'Failed to fetch global settings',
            );
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const updateGlobalSettings = async (
        data: UpdateGlobalSettingsRequest,
    ): Promise<GlobalSettings | null> => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await authenticatedApi.put(
                '/global-settings',
                data,
            );
            return response.data.data;
        } catch (err: any) {
            console.error('Error updating global settings:', err);
            setError(
                err.response?.data?.message ||
                    'Failed to update global settings',
            );
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const getGalleryUrl = async (): Promise<string | null> => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await authenticatedApi.get(
                '/global-settings/gallery-url',
            );
            return response.data.galleryUrl;
        } catch (err: any) {
            console.error('Error fetching gallery URL:', err);
            setError('Failed to fetch gallery URL');
            return null;
        }
    };
    return {
        getGlobalSettings,
        updateGlobalSettings,
        getGalleryUrl,
        isLoading,
        error,
    };
}
